'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ArrowUpRight } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

interface WorkCardProps {
  href: string;
  imageSrc: string;
  imageAlt: string;
  title: string;
  linkText?: string;
}

export default function WorkCard({ 
  href, 
  imageSrc, 
  imageAlt, 
  title, 
  linkText = "Read the article" 
}: WorkCardProps) {
  const pathname = usePathname();
  const fullImagePath = `/images/gallery/${imageSrc}`;
  
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    
    // Track the click
    const clickText = linkText || title;
    trackEvent('ai_click', {
      click_text: clickText,
      click_url: href,
      page_path: pathname
    });
    
    window.open(href, '_blank', 'noopener,noreferrer');
  };
  
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" className="w-48 block group cursor-pointer" onClick={handleClick}>
      <div className="aspect-[5/4] relative overflow-hidden rounded-3xl">
        <Image
          src={fullImagePath}
          alt={imageAlt}
          fill
          sizes="192px"
          className="object-cover transition-transform duration-300 lg:group-hover:scale-105 rounded-3xl"
        />
        <div className="absolute inset-0 bg-black bg-opacity-0 lg:group-hover:bg-opacity-30 transition-all duration-300" />
      </div>
      <div className="mt-3 mb-3">
        <h2 className="card-title-sm mb-2 line-clamp-3 pb-[0.1875rem]">{title}</h2>
        <div className="flex items-center gap-2">
          <span className="font-baikal font-light text-[0.875rem] border-b-2 border-red-500">{linkText}</span>
          <ArrowUpRight className="w-8 h-8 text-red-500" />
        </div>
      </div>
    </Link>
  );
}
